import { useEffect, useState } from 'react'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { Screen } from '../frame/SetupFrame'
import { useStepFooter } from '../../hooks/useStepFooter'
import { Input, Radio, Tile } from '../ui/primitives'
import {
   BUDGET_PRESETS,
   clampBudget,
   leadsFor,
   pricingOf,
   splitBudget,
} from '../../utils/budget'
import { seatCostFor } from '../../utils/seats'
import { plural } from '../../utils/format'

/**
 * Step 5: the monthly call budget.
 *
 * Three presets with the number of leads each lets us work, and a custom
 * amount. The amount is split into the new-lead and revival budgets the
 * payment step charges from, the engine's own split.
 */
export const BudgetStep = () => {
   const { onboardingData, getNextStep, setPricingData } =
      useOnboardingStoreV2()
   const pricing = pricingOf(onboardingData.pricingData)
   const budget = pricing.maxSpend
   const preset = BUDGET_PRESETS.find((p) => p.maxSpend === budget)
   const [custom, setCustom] = useState(preset ? '' : String(budget))

   // Old records carry no pricing: put the default on the record once.
   useEffect(() => {
      if (!onboardingData.pricingData?.maxSpend) setPricingData(pricing)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   const apply = (amount: number) =>
      setPricingData({ ...pricing, ...splitBudget(clampBudget(amount)) })
   const applyCustom = (v: string) => {
      const digits = v.replace(/\D/g, '').slice(0, 4)
      setCustom(digits)
      if (digits) apply(Number(digits))
   }
   const seats = pricing.seatsCount
   const seatCost = seatCostFor(seats)

   useStepFooter(`Continue with $${budget}/mo`, () => getNextStep(), {
      hint: `About ${leadsFor(budget)} leads worked each month, plus $${seatCost}/mo for ${plural(seats, 'seat')}.`,
      testId: 'continue-budget',
   })

   return (
      <Screen
         stepKey="budget"
         kind="budget"
         title="How much should we spend on calls each month?"
         lede="This pays for the calls and texts themselves, not seats. We only use what the leads need, unused balance rolls over, and you can change it any time."
         width="lg"
      >
         <div
            className="grid grid-cols-1 sm:grid-cols-3 gap-3"
            role="radiogroup"
         >
            {BUDGET_PRESETS.map((b, i) => {
               const on = !custom && budget === b.maxSpend
               return (
                  <Tile
                     key={b.id}
                     selected={on}
                     onClick={() => {
                        setCustom('')
                        apply(b.maxSpend)
                     }}
                     testId={`budget-${b.id}`}
                     className="p-5"
                  >
                     <div className="flex items-center justify-between">
                        <p className="text-[13px] font-semibold text-[var(--ob-text-2)]">
                           {b.label}
                           {i === 0 && (
                              <span className="ml-2 text-[11px] font-semibold text-[var(--ob-light)]">
                                 Most popular
                              </span>
                           )}
                        </p>
                        <Radio on={on} />
                     </div>
                     <p className="mt-2 text-[30px] leading-none font-semibold tracking-tight">
                        ${b.maxSpend}
                        <span className="text-[14px] font-normal text-[var(--ob-text-3)]">
                           /mo
                        </span>
                     </p>
                     <p className="mt-3 text-[14px]">
                        <b>{leadsFor(b.maxSpend)}</b> leads we can work
                     </p>
                     <p className="text-[12px] text-[var(--ob-text-3)] mt-0.5">
                        {b.blurb}
                     </p>
                  </Tile>
               )
            })}
         </div>
         <div className="mt-4 mx-auto max-w-[440px]">
            <Input
               label="Or type your own monthly budget"
               value={custom}
               onChange={applyCustom}
               placeholder="e.g. 400"
               leading={<span className="text-[15px] font-semibold">$</span>}
               testId="custom-budget"
               trailing={
                  custom ? `about ${leadsFor(budget)} leads` : 'per month'
               }
               inputMode="numeric"
               hint="Between $50 and $5,000 a month."
            />
         </div>
      </Screen>
   )
}
